import React from "react";
import Utils from "Utils/utils";
import MemberBreakdownSkeleton from "./MemberBreakdownSkeleton";

// subRowsByMemberId: optional Map<memberId, liAccountRows[]> - when passed, each member row that has
// matching entries renders them nested underneath (LinkedIn-account split of that member's own numbers)
// instead of the LI-account breakdown being a separate, flat, sibling list. Only ever populated for
// admins with both "Breakdown by member" and its "Split by LinkedIn account" sub-toggle on - non-admins
// never get memberBreakdown rows in the first place, so this stays null for them and the caller falls
// back to rendering the LI-account rows as their own flat list (their own accounts only).
const MemberBreakdownList = ({ rows, loading, mode = "count", heading, subRowsByMemberId }) => {
  if (loading) {
    return (
      <div className="widgetCardMemberBreakdown">
        {heading && <div className="widgetCardBreakdownHeading">{heading}</div>}
        <MemberBreakdownSkeleton />
      </div>
    );
  }
  if (!rows?.length) return null;
  const isRate = mode === "rate";
  const total = isRate ? 0 : rows.reduce((sum, r) => sum + (r.count || 0), 0);
  return (
    <div className="widgetCardMemberBreakdown">
      {heading && <div className="widgetCardBreakdownHeading">{heading}</div>}
      {rows.map(r => {
        const pct = isRate
          ? Math.min(100, Math.max(0, Math.round(r.rate || 0)))
          : total > 0
          ? Math.min(100, Math.round(((r.count || 0) / total) * 100))
          : 0;
        const valueLabel = isRate ? `${(r.rate || 0).toFixed(1)}%` : Utils.formatNumberToStringInUSFormat(r.count);
        const subRows = subRowsByMemberId?.get(r.memberId) || [];
        return (
          <div key={r.memberId} className="widgetCardMemberBreakdownRow">
            <div className="widgetCardMemberBreakdownHeader">
              <span className="widgetCardMemberBreakdownName">{r.name || r.memberId}</span>
              <span className="widgetCardMemberBreakdownValue">
                {valueLabel}
                {isRate && r.denominator != null && (
                  <span className="widgetCardMemberBreakdownSub"> · {Utils.formatNumberToStringInUSFormat(r.numerator)}/{Utils.formatNumberToStringInUSFormat(r.denominator)}</span>
                )}
              </span>
            </div>
            <div className="widgetCardMemberBreakdownBarTrack">
              <div className="widgetCardMemberBreakdownBarFill" style={{ width: `${pct}%` }} />
            </div>
            {subRows.length > 0 && (
              <div className="widgetCardMemberBreakdownSubList">
                {subRows.map(sr => {
                  const subPct = isRate
                    ? Math.min(100, Math.max(0, Math.round(sr.rate || 0)))
                    : r.count > 0
                    ? Math.min(100, Math.round(((sr.count || 0) / r.count) * 100))
                    : 0;
                  const subValueLabel = isRate
                    ? `${(sr.rate || 0).toFixed(1)}%`
                    : Utils.formatNumberToStringInUSFormat(sr.count);
                  return (
                    <div key={sr.liAccountId} className="widgetCardMemberBreakdownSubRow">
                      <div className="widgetCardMemberBreakdownSubHeader">
                        <span className="widgetCardMemberBreakdownSubName">{sr.name || sr.liAccountId}</span>
                        <span className="widgetCardMemberBreakdownSubValue">{subValueLabel}</span>
                      </div>
                      <div className="widgetCardMemberBreakdownSubBarTrack">
                        <div className="widgetCardMemberBreakdownSubBarFill" style={{ width: `${subPct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MemberBreakdownList;
